import { useId } from 'react';

import type { ConfigurationParameter } from './ConfigurationSchemaEditor';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface ToolConfigurationFormProps {
    /** Schema defining which parameters to show (id, name, type) */
    schema: ConfigurationParameter[];
    /** Current values keyed by parameter id */
    value: Record<string, string>;
    /** Called when any value changes */
    onChange: (nextValues: Record<string, string>) => void;
    /** Optional: disable all fields */
    disabled?: boolean;
}

/**
 * Renders a form to enter configuration values based on a configuration schema.
 * Reusable for both: owner config (AgentToolsetManagement) and tenant config (McpConnectModal / orders).
 */
export default function ToolConfigurationForm({
    schema,
    value,
    onChange,
    disabled = false,
}: ToolConfigurationFormProps) {
    const fieldId = useId();

    const handleChange = (paramId: string, newVal: string) => {
        onChange({ ...value, [paramId]: newVal });
    };

    if (!schema || schema.length === 0) {
        return null;
    }

    return (
        <div className="flex max-w-[480px] flex-col gap-5">
            {schema.map((param) => {
                const isPassword = param.type === 'password';
                const inputType = isPassword ? 'password' : param.type === 'integer' || param.type === 'decimal' ? 'number' : 'text';
                const numProps = param.type === 'integer'
                    ? { min: 0, step: 1 }
                    : param.type === 'decimal'
                        ? { min: 0, step: 'any' }
                        : {};
                const inputId = `${fieldId}-${param.id}`;

                return (
                    <div key={param.id} className="flex flex-col gap-1.5">
                        <Label htmlFor={inputId}>{param.name}</Label>
                        <Input
                            id={inputId}
                            value={value[param.id] ?? ''}
                            onChange={(event) => handleChange(param.id, event.target.value ?? '')}
                            type={inputType}
                            disabled={disabled}
                            placeholder={param.name}
                            {...numProps}
                        />
                    </div>
                );
            })}
        </div>
    );
}
